<template>
  <div>
    <!-- Sidebar that display when a chapter or entry is selected -->
    <sidebar-menu
      :menu="entrySelectedMenu"
      @collapse="onCollapse"
      :collapsed="isSideBarExpanded"
      @item-click="onItemClick"
      v-if="clickedChapterId !== null || clickedEntryId !== null"
    />

    <!-- Sidebar that display when no chapter/entry is selected -->
    <sidebar-menu
      :menu="menu"
      @collapse="onCollapse"
      :collapsed="isSideBarExpanded"
      @item-click="onItemClick"
      v-else
    />

    <!-- Chapter Rename -->
    <SweetModal title="Rename Chapter" ref="renameChapterModal" :blocking="true" :pulse-on-block="false">
      <input type="text" class="form-control" placeholder="Chapter Title" v-model="renameChapterName"
             v-on:keyup.enter="renameChapter">

      <div slot="button">
        <button class="btn btn-primary" @click="renameChapter">Save</button>
      </div>
    </SweetModal>

    <!-- Chapter Delete -->
    <SweetModal :title="isEntrySelected ? $t('UI_DELETE_ENTRY_MODAL_TITLE') : $t('UI_DELETE_CHAPTER_MODAL_TITLE')"
                ref="chapterDeleteModal" icon="warning" :blocking="true" :pulse-on-block="false">
      <p>{{ isEntrySelected ? $t('UI_DELETE_ENTRY_MODAL_TEXT') : $t('UI_DELETE_CHAPTER_MODAL_TEXT') }}</p>

      <div slot="button">
        <button class="btn btn-danger" @click="deleteChapter">Delete</button>
        <button class="btn btn-dark ml-1" @click="$refs.chapterDeleteModal.close()">Cancel</button>
      </div>
    </SweetModal>

    <!-- Chapter Create  -->
    <SweetModal :title="isAnythingSelected ? $t('UI_CREATE_ENTRY_MODAL_TITLE') : $t('UI_CREATE_CHAPTER_MODAL_TITLE')"
                ref="chapterCreateModal" v-on:open="$nextTick(() => $refs.addChapterInput.focus())" :blocking="true" :pulse-on-block="false">
      <input type="text" class="form-control" placeholder="Title" v-model="addChapterName" v-on:keyup.enter="addChapter"
             ref="addChapterInput">

      <div slot="button">
        <button class="btn btn-primary" @click="addChapter">Create</button>
        <button class="btn btn-dark ml-1" @click="$refs.chapterCreateModal.close()">Cancel</button>
      </div>
    </SweetModal>
  </div>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';

import { RepositoryFactory } from '../../../../../../repositories/RepositoryFactory';

const ChapterRepository = RepositoryFactory.get('chapters');
const InstanceRepository = RepositoryFactory.get('instances');
const HistoryRepository = RepositoryFactory.get('history');

export default {
  name: 'DefaultSidebarMenuComponent',
  components: { SweetModal },
  computed: {
    clickedChapterId() {
      return this.$store.getters.getSelectedChapterId;
    },
    clickedEntryId() {
      return this.$store.getters.getSelectedEntryId;
    },

    isAnythingSelected() {
      return this.clickedEntryId !== null || this.clickedChapterId != null;
    },
    isEntrySelected() {
      return this.clickedEntryId !== null;
    },
    isChapterSelected() {
      return this.clickedChapterId !== null;
    },
    isSideBarExpanded() {
      return this.$store.getters.isSidebarExpanded;
    },
  },
  methods: {
    onCollapse(collapsed) {
      this.$store.dispatch('expandSidebar', collapsed);
    },
    onItemClick(event, item) {
      switch (item.id) {
        case 'undo':
          this.undoLastAction();
          break;
        case 'moveEnable':
          this.enableMoveMode();
          break;
        case 'timeshiftEnable':
          this.enableTimeShift();
          break;
        case 'addChapter':
          this.openChapterCreateModal();
          break;
        case 'renameChapter':
          this.openChapterRenameModal();
          break;
        case 'deleteChapter':
          this.openChapterDeleteModal();
          break;
        default:
          break;
      }
    },

    /*
              ======= ACTIONS =======
            */

    /**
       * Undo the last Action
       * @returns {Promise<void>}
       */
    async undoLastAction() {
      await HistoryRepository.rewind();
    },

    /**
       * Enables or Disables the move mode which allows users to move chapters & entries
       */
    enableMoveMode() {
      this.$store.commit('setChapterEntryMode', 'move');
    },

    enableTimeShift() {
      this.$store.commit('setChapterEntryMode', 'timeshift');
    },

    /**
       * Opens a Modal to Create a Chapter
       */
    openChapterCreateModal() {
      this.$refs.chapterCreateModal.open();
    },

    /**
       * Add a Chapter
       * @returns {Promise<boolean>}
       */
    async addChapter() {
      if (this.addChapterName.length < 1) {
        return false;
      }

      await ChapterRepository.addChapter(
        this.addChapterName,
        this.clickedChapterId, // Either null or ID of Parent Chapter
      )
        .then(() => {
          InstanceRepository.refetchActive(false);
          if (this.$store.getters.isChapterSelected || this.$store.getters.isEntrySelected) {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MENU_ENTRY_ADDED', { name: this.addChapterName }));
          } else {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MENU_CHAPTER_ADDED', { name: this.addChapterName }));
          }
          this.addChapterName = '';
        });
      this.$refs.chapterCreateModal.close();
    },


    async renameChapter() {
      await ChapterRepository.renameChapter(
        this.clickedChapterId,
        this.renameChapterName,
        this.clickedEntryId,
      )
        .then(() => {
          InstanceRepository.refetchActive(false);

          if (this.$store.getters.isChapterSelected) {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MNUE_INSTANCE_RENAMED_CHAPTER'));
          } else if (this.$store.getters.isEntrySelected) {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MNUE_INSTANCE_RENAMED_ENTRY'));
          }
        });
      this.$refs.renameChapterModal.close();
    },

    async deleteChapter() {
      await ChapterRepository.delete(
        this.clickedChapterId,
        this.clickedEntryId,
      )
        .then(() => {
          InstanceRepository.refetchActive(false);

          // Unselect selected Chapter & Entry
          this.$store.commit('setSelectedChapterId', null);
          this.$store.commit('setSelectedEntryId', null);

          if (this.$store.getters.isChapterSelected) {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MENU_INSTANCE_DELETED_CHAPTER'));
          } else if (this.$store.getters.isEntrySelected) {
            this.$snotify.info(this.$t('UI_INSTANCE_FAB_MENU_INSTANCE_DELETED_ENTRY'));
          }
        });
      this.$refs.chapterDeleteModal.close();
    },

    openChapterDeleteModal() {
      this.$refs.chapterDeleteModal.open();
    },

    openChapterRenameModal() {
      this.renameChapterName = '';
      const allChapters = this.$store.getters.getActiveInstanceChapters;

      if (!this.isAnythingSelected) return false;

      if (this.isEntrySelected) {
        const entry = allChapters.flatMap(chapter => chapter.entries).filter(entry => entry.id === this.clickedEntryId).shift();
        if (entry !== null) {
          this.renameChapterName = entry.text;
          this.$refs.renameChapterModal.open();
        }
      } else if (this.isChapterSelected) {
        const chapter = this.$store.getters.getActiveInstanceChapters.find(chapter => chapter.id === this.clickedChapterId);
        if (chapter !== null) {
          this.renameChapterName = chapter.title;
          this.$refs.renameChapterModal.open();
        }
      }
      return false;
    },
  },
  data() {
    return {
      renameChapterName: '',
      addChapterName: '',

      menu: [
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_ADD_CHAPTER'),
          icon: 'fa fa-plus',
          id: 'addChapter',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_MOVE_ENABLE'),
          icon: 'fa fa-arrows-alt',
          id: 'moveEnable',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_TIMESHIFT_ENABLE'),
          icon: 'fa fa-stopwatch',
          id: 'timeshiftEnable',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_UNDO'),
          icon: 'fa fa-undo',
          id: 'undo',
        },
      ],
      entrySelectedMenu: [
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_ADD_ENTRY'),
          icon: 'fa fa-plus',
          id: 'addChapter',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_RENAME_ENTRY'),
          icon: 'fa fa-edit',
          id: 'renameChapter',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_DELETE_ENTRY'),
          icon: 'fa fa-trash',
          id: 'deleteChapter',
        },
        {
          title: this.$t('UI_INSTANCE_FAB_MENU_UNDO'),
          icon: 'fa fa-undo',
          id: 'undo',
        },
      ],

    };
  },
};
</script>

<style scoped>

</style>
